//mushroom-orders-page.tsx

import React, { useEffect, useState, useContext, useRef } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  useGridApiRef,
  GridRowParams,
  GridRowClassNameParams,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import {
  Button,
  CircularProgress,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";
import ConfirmDialog from "../components/confirm-dialog";
import { ErrorContext, UserContext } from "../components/app-routes";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { useHistory } from "react-router-dom";

// --------------------
// Local storage keys
// --------------------
const mushroomOrdersStorageKey = "mushroomOrders";
const mushroomOrdersGridStateStorageKey = "mushroomOrdersGridState";
const mushroomOrdersGridDensityStorageKey = "mushroomOrdersGridDensity";
const showArchivedCheckedStorageKey = "mushroomOrdersShowArchivedChecked";
const showActiveCheckedStorageKey = "mushroomOrdersShowActiveChecked";

const mushroomCustomersStorageKey = "mushroomCustomers";
const mushroomProductsStorageKey = "mushroomProducts";

// --------------------
// Types (MVP/local)
// --------------------
type MushroomOrder = {
  id: number;
  customerName: string;
  orderDate: string; // yyyy-LL-dd
  products: string[]; // product names
  notes: string | null;
  archivedAt: string | null;
};

// --------------------
// Local storage helpers
// --------------------
function loadMushroomOrders(): MushroomOrder[] {
  const raw = window.localStorage.getItem(mushroomOrdersStorageKey);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MushroomOrder[]) : [];
  } catch {
    return [];
  }
}

function saveMushroomOrders(orders: MushroomOrder[]) {
  window.localStorage.setItem(mushroomOrdersStorageKey, JSON.stringify(orders));
}

function trashMushroomOrder(id: number) {
  const orders = loadMushroomOrders();
  const now = DateTime.local().toISO();
  const next = orders.map((o) => (o.id === id ? { ...o, archivedAt: now } : o));
  saveMushroomOrders(next);
}

function loadCustomerNames(): string[] {
  try {
    const raw = window.localStorage.getItem(mushroomCustomersStorageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as any[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((c) => String(c?.name ?? "").trim()).filter((v) => !!v);
  } catch {
    return [];
  }
}

function loadProductNames(): string[] {
  try {
    const raw = window.localStorage.getItem(mushroomProductsStorageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as any[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((p) => String(p?.name ?? "").trim()).filter((v) => !!v);
  } catch {
    return [];
  }
}

// --------------------
// Grid columns
// --------------------
const columns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    hideable: false,
    headerName: "Checkbox",
  },
  {
    field: "archived",
    headerName: "",
    width: 35,
    minWidth: 35,
    sortable: false,
    filterable: false,
    align: "center",
    disableColumnMenu: true,
    disableExport: true,
    renderCell: (params) =>
      params.row.archivedAt ? (
        <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
      ) : null,
  },
  {
    field: "customerName",
    headerName: "Customer",
    width: 220,
  },
  {
    field: "orderDate",
    headerName: "Order Date",
    width: 160,
    minWidth: 160,
    valueFormatter: (p: any) => {
      const s = p?.value ? String(p.value) : "";
      const dt = DateTime.fromFormat(s, "yyyy-LL-dd");
      return dt.isValid ? dt.toFormat("MMM dd, yyyy") : s;
    },
  },
  {
    field: "products",
    headerName: "Products",
    flex: 1,
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 220,
  },
];

const OrdersDataGrid = React.memo(
  (props: {
    orders: MushroomOrder[] | null | undefined;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    initialPage?: number;
  }) => {
    const history = useHistory();
    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(mushroomOrdersGridStateStorageKey);

        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;

            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }

        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 15 },
          };
        }

        localStorage.setItem(
          mushroomOrdersGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(mushroomOrdersGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(mushroomOrdersGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "harvestDate", sort: "asc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        mushroomOrdersGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const context = useContext(UserContext);
    const grower = context.grower;
    if (!grower) throw new Error("Invalid context");

    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);

      return `SeedLeaf Mushroom Orders - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    if (!props.orders) return null;
    if (!initialState) return <CircularProgress />;

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.orders.map((o) => ({
          id: o.id,
          customerName: o.customerName,
          orderDate: o.orderDate,
          products: (o.products ?? []).join(", "),
          notes: o.notes ?? "",
          archivedAt: o.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [{ field: "harvestDate", sort: "asc" }],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{ toolbar: GridToolbar }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(mushroomOrdersGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: { allColumns: true, fileName: csvFileName },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) return "archived-row";
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          // placeholder: later route to /mushroom/home/orders/:id
          return;
        }}
      />
    );
  },
);

export default function MushroomOrdersPage() {
  const [orders, setOrders] = useState<MushroomOrder[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const { handleError } = useContext(ErrorContext);

  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const history = useHistory();
  const isMounted = useRef(true);

  const [addOpen, setAddOpen] = useState(false);
  const [addCustomerName, setAddCustomerName] = useState("");
  const [addOrderDate, setAddOrderDate] = useState(
    DateTime.local().toFormat("yyyy-LL-dd"),
  );
  const [addProductNames, setAddProductNames] = useState<string[]>([]);
  const [addNotes, setAddNotes] = useState("");

  const [customerOptions, setCustomerOptions] = useState<string[]>([]);
  const [productOptions, setProductOptions] = useState<string[]>([]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    async function getOrders() {
      setBusy(true);

      try {
        let all = loadMushroomOrders();

        if (!all.length) {
          const today = DateTime.local().toFormat("yyyy-LL-dd");

          all = [
            {
              id: 1,
              customerName: "Coastal Market",
              orderDate: today,
              products: ["Oyster – 150g Pack"],
              notes: "Deliver Tue",
              archivedAt: null,
            },
            {
              id: 2,
              customerName: "Forest Café",
              orderDate: DateTime.local()
                .plus({ days: 1 })
                .toFormat("yyyy-LL-dd"),
              products: ["Lion’s Mane – 150g Pack"],
              notes: null,
              archivedAt: null,
            },
            {
              id: 3,
              customerName: "Sunrise Foods",
              orderDate: DateTime.local()
                .minus({ days: 2 })
                .toFormat("yyyy-LL-dd"),
              products: ["Mixed Mushrooms – 1lb Bulk"],
              notes: "Wholesale",
              archivedAt: DateTime.local().minus({ days: 7 }).toISO(),
            },
          ];

          saveMushroomOrders(all);
        }

        if (isMounted.current) setOrders(all);
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    }

    getOrders();
  }, [handleError]);

  const handleSelectionChange = (m: GridRowSelectionModel): void => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[ids.length - 1]] : []);
  };

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setTrashDialogOpen(false);
    if (!okTrash) return;

    setBusy(true);

    let trashed = 0;
    for (let i = 0; i < selected.length; i++) {
      try {
        trashMushroomOrder(selected[i]);
        trashed++;
        mixpanel.track("Delete Mushroom Order");
      } catch (error) {
        if (isMounted.current) handleError(error);
        break;
      }
    }

    try {
      const all = loadMushroomOrders();
      if (isMounted.current) {
        setOrders(all);
        setSelected([]);
      }
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleClickAdd = () => {
    const customers = loadCustomerNames();
    const products = loadProductNames();

    setCustomerOptions(customers);
    setProductOptions(products);

    setAddCustomerName("");
    setAddOrderDate(DateTime.local().toFormat("yyyy-LL-dd"));
    setAddProductNames([]);
    setAddNotes("");
    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = () => {
    if (!orders) return;
    if (!addCustomerName.trim()) return;
    if (!addOrderDate.trim()) return;

    setBusy(true);

    try {
      const current = loadMushroomOrders();
      const maxId = current.reduce((m, o) => Math.max(m, o.id), 0);

      const next: MushroomOrder = {
        id: maxId + 1,
        customerName: addCustomerName.trim(),
        orderDate: addOrderDate,
        products: addProductNames,
        notes: addNotes.trim() ? addNotes.trim() : null,
        archivedAt: null,
      };

      const updated = [next, ...current];
      saveMushroomOrders(updated);
      setOrders(updated);
      setAddOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const page = getSearchParamNumber(history, "page", 0);

  return (
    <Page title={"Orders"}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleClickAdd}
          disabled={busy}
        >
          Add
        </Button>

        <IconButton
          aria-label="delete"
          disabled={busy || selected.length === 0}
          onClick={() => setTrashDialogOpen(true)}
          size="large"
        >
          <DeleteIcon />
        </IconButton>

        <ActiveFilterControls
          showActiveChecked={showActiveChecked}
          showArchivedChecked={showArchivedChecked}
          onChangeShowActive={handleShowActiveChanged}
          onChangeShowArchived={handleShowArchivedChanged}
        />
      </Box>

      <Box sx={{ height: 750, width: "100%", pt: 2 }}>
        <OrdersDataGrid
          orders={orders?.filter(
            (o) =>
              (showActiveChecked && o.archivedAt === null) ||
              (showArchivedChecked && o.archivedAt !== null),
          )}
          onSelectionChange={handleSelectionChange}
          initialPage={page}
          onPaginationModelChange={(m) => {
            const params = new URLSearchParams(`page=${m.page}`);
            history.replace({ search: params.toString() });
          }}
        />
      </Box>

      <ConfirmDialog
        title="Trash Orders"
        description={`Are you sure you want to trash ${
          selected.length
        } order${selected.length > 1 ? "s" : ""}?`}
        open={trashDialogOpen}
        onClose={handleTrashDialogClose}
      />

      <Dialog open={addOpen} onClose={handleCloseAdd} fullWidth maxWidth="sm">
        <DialogTitle>Add Order</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Customer"
              value={addCustomerName}
              onChange={(e) => setAddCustomerName(e.target.value)}
              select={customerOptions.length > 0}
              fullWidth
            >
              {customerOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Order Date"
              type="date"
              value={addOrderDate}
              onChange={(e) => setAddOrderDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Products"
              value={addProductNames.join(", ")}
              onChange={(e) =>
                setAddProductNames(
                  e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => !!s),
                )
              }
              select={productOptions.length > 0}
              fullWidth
              helperText={
                productOptions.length > 0
                  ? "Select one (or type comma-separated)"
                  : "Type comma-separated"
              }
            >
              {productOptions.map((p) => (
                <MenuItem
                  key={p}
                  value={p}
                  onClick={() => {
                    const exists = addProductNames.includes(p);
                    const next = exists
                      ? addProductNames.filter((x) => x !== p)
                      : [...addProductNames, p];
                    setAddProductNames(next);
                  }}
                >
                  {p}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Notes"
              value={addNotes}
              onChange={(e) => setAddNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdd} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAdd}
            color="primary"
            variant="contained"
            disabled={busy || !addCustomerName.trim() || !addOrderDate.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
